import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { alpha } from '@mui/material/styles';
import { CANVAS_W, CANVAS_H, HOTSPOT_KEYFRAMES } from './constants';
import { getHotspotCentroid } from './helpers';

// ── Bounding box of a hotspot in canvas pixels ───────────────────────────────
function getHotspotBBox(hotspot, bpCanvasW, bpCanvasH) {
  const pxX = p => (p / 100) * bpCanvasW;
  const pxY = p => (p / 100) * bpCanvasH;
  if (hotspot.shape === 'rect' && hotspot.points?.[0] && hotspot.meta) {
    return { x: pxX(hotspot.points[0].x), y: pxY(hotspot.points[0].y), w: pxX(hotspot.meta.width || 10), h: pxY(hotspot.meta.height || 10) };
  }
  if (hotspot.shape === 'circle' && hotspot.points?.[0]) {
    const cx = pxX(hotspot.points[0].x), cy = pxY(hotspot.points[0].y);
    const r  = pxX(hotspot.meta?.radius || 5);
    return { x: cx - r, y: cy - r, w: r * 2, h: r * 2 };
  }
  const pts = hotspot.points || [];
  if (!pts.length) return null;
  const xs = pts.map(p => pxX(p.x)), ys = pts.map(p => pxY(p.y));
  return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
}

// ── Pill indicator: dot that expands into a label pill in showcase mode ───────
// Normal state  : small circle (the "dot") with pulse + ring animations
// Showcase state: dot animates to a full-width pill; dot hides; label fades in
//
// The pill is a <rect> that morphs from dotR×dotR (circle-like, rx=dotR) to
// pillW×pillH (wide, rx=pillH/2). CSS transitions on SVG geometry attributes
// drive the expansion; the label text fades in on top with a small delay.
function PulseIndicator({ cx, cy, color, hovered, showcaseMode, label }) {
  // Fixed 12px text — readable on all canvas sizes without scaling
  const FONT_SIZE  = 12;
  const PILL_H     = 24;           // pill height (px in canvas space)
  const DOT_R      = 10.5;         // radius of the dot
  const H_PAD      = 10;           // horizontal text padding inside pill

  const maxChars   = 24;
  const displayLabel = (label || '').length > maxChars
    ? label.slice(0, maxChars - 1) + '…'
    : (label || '');

  // Approximate pill width from text length (FONT_SIZE * 0.62 per char + padding)
  const textW  = displayLabel.length * FONT_SIZE * 0.62;
  const pillW  = Math.max(textW + H_PAD * 2, DOT_R * 2);
  const pillH  = PILL_H;
  const pillRx = pillH / 2;

  // Dot geometry (circle expressed as rect: x,y centred, width=height=2*r, rx=r)
  const dotX = cx - DOT_R;
  const dotY = cy - DOT_R;
  const dotW = DOT_R * 2;
  const dotH = DOT_R * 2;

  // Pill geometry (centred on same cx,cy)
  const pX  = cx - pillW / 2;
  const pY  = cy - pillH / 2;

  // Animated rect values — transitions between dot and pill state
  const rX   = showcaseMode ? pX   : dotX;
  const rY   = showcaseMode ? pY   : dotY;
  const rW   = showcaseMode ? pillW : dotW;
  const rH   = showcaseMode ? pillH : dotH;
  const rRx  = showcaseMode ? pillRx : DOT_R;

  const SPRING = '0.55s cubic-bezier(0.34, 1.4, 0.64, 1)';

  return (
    <g pointerEvents="none">
      {/* Expanding ring — hides in showcase (dot is replaced by pill) */}
      <circle
        cx={cx} cy={cy} r={DOT_R}
        fill="none" stroke={color} strokeWidth={1.5} opacity={0}
        style={{
          opacity: showcaseMode ? 0 : 0, // always 0, animated by keyframe below
          animation: (hovered || showcaseMode) ? 'none' : 'hs-ring 2s ease-out infinite',
          animationDelay: '0.4s',
        }}
      />

      {/* The pill/dot rect — morphs between circle and pill via CSS transition */}
      <rect
        x={rX} y={rY} width={rW} height={rH} rx={rRx} ry={rRx}
        fill={color}
        stroke="#ffffff"
        strokeWidth={1.5}
        style={{
          transition: `x ${SPRING}, y ${SPRING}, width ${SPRING}, height ${SPRING}, rx ${SPRING}, ry ${SPRING}`,
          // Pulse animation only in dot mode
          animation: (hovered || showcaseMode) ? 'none' : 'hs-pulse 2s ease-in-out infinite',
        }}
      />

      {/* Label text — fades in once pill has expanded */}
      {displayLabel && (
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#ffffff"
          fontSize={FONT_SIZE}
          fontWeight={700}
          fontFamily="inherit"
          style={{
            opacity: showcaseMode ? 1 : 0,
            transition: `opacity 0.25s ease ${showcaseMode ? '0.3s' : '0s'}`,
            letterSpacing: '0.02em',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        >
          {displayLabel}
        </text>
      )}
    </g>
  );
}

// ── Single hotspot shape ───────────────────────────────────────────────────────
export function HotspotShape({ hotspot, isClickable, onClick, shimmerDelay, onHover, onMove, onLeave, bpCanvasW = CANVAS_W, bpCanvasH = CANVAS_H, showcaseMode = false }) {
  const [hovered, setHovered] = useState(false);

  const glowColor    = hotspot.glowColor         || '#1075BA';
  const fillColor    = hotspot.hoverFillColor    || glowColor;
  const fillOpacity  = hotspot.hoverFillOpacity  ?? 0.2;
  const bordColor    = hotspot.hoverBorderColor  || glowColor;
  const bordOpacity  = hotspot.hoverBorderOpacity ?? 0.85;
  const sw           = hotspot.strokeWidth       ?? 1.5;
  const glowOpacity  = hotspot.glowOpacity       ?? 0.5;
  const isActive     = hotspot.isActive !== false;

  const showStyle = (hovered && isClickable) || showcaseMode;
  const effectiveFillOpacity = showcaseMode
    ? fillOpacity * 0.5
    : (showStyle ? fillOpacity : 0);
  const fill      = alpha(fillColor, effectiveFillOpacity);
  const stroke    = alpha(bordColor, showStyle ? bordOpacity : 0);
  const glowFilter = showStyle && glowOpacity > 0
    ? `drop-shadow(0 0 8px ${alpha(glowColor, glowOpacity)})`
    : 'none';

  const TRANS = 'fill 0.6s ease, stroke 0.6s ease, filter 0.6s ease';
  const groupStyle = {
    opacity: isActive ? 1 : 0.3,
    cursor: isClickable ? 'pointer' : 'default',
    // Re-enable pointer events on each shape group — the parent SVG has
    // pointerEvents:none to avoid blocking DOM elements beneath it (like
    // the exhibitor grid). Individual shape groups opt back in so hotspot
    // hover/click still works.
    pointerEvents: 'all',
  };
  const shapeTransition = { transition: TRANS };

  const pxX = p => (p / 100) * bpCanvasW;
  const pxY = p => (p / 100) * bpCanvasH;

  const hasShimmer  = shimmerDelay !== null && shimmerDelay !== undefined;
  const shimmerStyle = hasShimmer
    ? { animation: 'hs-shape-shimmer 0.8s ease-in-out forwards', animationDelay: `${shimmerDelay}ms` }
    : null;

  const handlers = {
    ...(isClickable ? { onClick: e => { e.stopPropagation(); onClick(hotspot); } } : {}),
    onMouseEnter: e => { setHovered(true);  onHover?.(hotspot.label, e); },
    onMouseMove:  e => { onMove?.(e); },
    onMouseLeave: e => { setHovered(false); onLeave?.(); },
  };

  const pulseProps = {
    color: bordColor,
    hovered: hovered && isClickable,
    showcaseMode,
    label: hotspot.label,
  };

  // ── Rect ──
  if (hotspot.shape === 'rect' && hotspot.points?.[0] && hotspot.meta) {
    const x = pxX(hotspot.points[0].x), y = pxY(hotspot.points[0].y);
    const w = pxX(hotspot.meta.width || 10), h = pxY(hotspot.meta.height || 10);
    return (
      <g style={groupStyle} {...handlers}>
        <rect x={x} y={y} width={w} height={h} fill={fill} stroke={stroke} strokeWidth={sw} rx={4} style={{ ...shapeTransition, filter: glowFilter }} />
        {shimmerStyle && <rect x={x} y={y} width={w} height={h} rx={4} fill={fillColor} stroke="none" opacity={0} style={shimmerStyle} pointerEvents="none" />}
        <PulseIndicator cx={x + w / 2} cy={y + h / 2} {...pulseProps} />
      </g>
    );
  }

  // ── Circle ──
  if (hotspot.shape === 'circle' && hotspot.points?.[0]) {
    const cx = pxX(hotspot.points[0].x), cy = pxY(hotspot.points[0].y);
    const r  = pxX(hotspot.meta?.radius || 5);
    return (
      <g style={groupStyle} {...handlers}>
        <circle cx={cx} cy={cy} r={r} fill={fill} stroke={stroke} strokeWidth={sw} style={{ ...shapeTransition, filter: glowFilter }} />
        {shimmerStyle && <circle cx={cx} cy={cy} r={r} fill={fillColor} stroke="none" opacity={0} style={shimmerStyle} pointerEvents="none" />}
        <PulseIndicator cx={cx} cy={cy} {...pulseProps} />
      </g>
    );
  }

  // ── Bezier path ──
  if (hotspot.shape === 'polygon' && hotspot.meta?.isBezier && hotspot.meta?.pathD) {
    const pts = hotspot.points || [];
    const xs  = pts.map(p => pxX(p.x)), ys = pts.map(p => pxY(p.y));
    const cx  = pts.length ? (Math.min(...xs) + Math.max(...xs)) / 2 : 0;
    const cy  = pts.length ? (Math.min(...ys) + Math.max(...ys)) / 2 : 0;
    return (
      <g style={groupStyle} {...handlers}>
        <path d={hotspot.meta.pathD} fill={fill} stroke={stroke} strokeWidth={sw} style={{ ...shapeTransition, filter: glowFilter }} />
        {shimmerStyle && <path d={hotspot.meta.pathD} fill={fillColor} stroke="none" opacity={0} style={shimmerStyle} pointerEvents="none" />}
        <PulseIndicator cx={cx} cy={cy} {...pulseProps} />
      </g>
    );
  }

  // ── Polygon ──
  if (hotspot.points?.length > 1) {
    const ptsStr = hotspot.points.map(p => `${pxX(p.x)},${pxY(p.y)}`).join(' ');
    const xs     = hotspot.points.map(p => pxX(p.x)), ys = hotspot.points.map(p => pxY(p.y));
    const cx     = (Math.min(...xs) + Math.max(...xs)) / 2;
    const cy     = (Math.min(...ys) + Math.max(...ys)) / 2;
    return (
      <g style={groupStyle} {...handlers}>
        <polygon points={ptsStr} fill={fill} stroke={stroke} strokeWidth={sw} style={{ ...shapeTransition, filter: glowFilter }} />
        {shimmerStyle && <polygon points={ptsStr} fill={fillColor} stroke="none" opacity={0} style={shimmerStyle} pointerEvents="none" />}
        <PulseIndicator cx={cx} cy={cy} {...pulseProps} />
      </g>
    );
  }

  return null;
}

// ── EngTextElement ─────────────────────────────────────────────────────────────
export function EngTextElement({ hotspot, canvasScale = 1, bpCanvasW = CANVAS_W, showcaseMode = false }) {
  const isActive = hotspot.isActive !== false;
  if (!isActive) return null;

  const x = hotspot.points?.[0]?.x ?? 10;
  const y = hotspot.points?.[0]?.y ?? 10;
  const w = hotspot.meta?.width    ?? 35;
  const h = hotspot.meta?.height   ?? 12;

  const text          = hotspot.meta?.text          ?? '';
  // fontSize is stored in desktop-canvas units (relative to CANVAS_W=1440).
  // Scale it proportionally to the current canvas coordinate space so text
  // appears the same visual size across all breakpoints. On desktop this is
  // a no-op (bpCanvasW/CANVAS_W = 1). On mobile (bpCanvasW=375) the desktop
  // font value is reduced proportionally: e.g. 40 * (375/1440) ≈ 10.4px.
  const fontSizeRaw   = hotspot.meta?.fontSize  ?? 25;
  const fontSize      = fontSizeRaw * canvasScale * (bpCanvasW / CANVAS_W);
  const fontWeight    = hotspot.meta?.fontWeight     ?? 600;
  const color         = hotspot.meta?.color          ?? '#ffffff';
  const textAlign     = hotspot.meta?.textAlign      ?? 'center';
  const bgColor       = hotspot.meta?.bgColor        ?? 'rgba(0,0,0,0.7)';
  const bgStrokeColor = hotspot.meta?.bgStrokeColor  ?? '#ffffff';
  const bgStrokeWidth = (hotspot.meta?.bgStrokeWidth ?? 3) * canvasScale;
  const borderRadius  = (hotspot.meta?.borderRadius  ?? 6) * canvasScale;

  return (
    <>
      <style>{`
        @keyframes eng-text-reveal {
          0%   { transform: scale(0.82); opacity: 0; }
          60%  { transform: scale(1.03); opacity: 1; }
          100% { transform: scale(1);    opacity: 1; }
        }
        @keyframes eng-text-breathe {
          0%   { transform: scale(1);     }
          50%  { transform: scale(0.982); }
          100% { transform: scale(1.012); }
        }
      `}</style>
      <div
        style={{
          position: 'absolute',
          left:   `${x}%`,
          top:    `${y}%`,
          width:  `${w}%`,
          height: `${h}%`,
          zIndex: 5 + (hotspot.order ?? 0),
          background:   showcaseMode ? 'transparent' : bgColor,
          border:       (showcaseMode || bgStrokeWidth <= 0) ? 'none' : `${bgStrokeWidth}px solid ${bgStrokeColor}`,
          borderRadius: `${borderRadius}px`,
          display:      'flex',
          alignItems:   'center',
          justifyContent: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center',
          padding:      '0 6px',
          boxSizing:    'border-box',
          overflow:     'hidden',
          pointerEvents: 'none',
          userSelect:   'none',
          opacity:      0,
          animation:    'eng-text-reveal 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.5s forwards, eng-text-breathe 4s ease-in-out 1.05s infinite alternate',
          transformOrigin: 'center center',
          transition:   'background 0.6s ease, border 0.6s ease',
        }}
      >
        <div style={{ width: '100%', color, fontWeight, fontSize: `${fontSize}px`, textAlign, lineHeight: 1.3, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
          {text}
        </div>
      </div>
    </>
  );
}

// ── Full SVG overlay layer ────────────────────────────────────────────────────
export function HotspotSVGLayer({ hotspots, isClickable, onHotspotClick, bpCanvasW = CANVAS_W, bpCanvasH = CANVAS_H, showcaseMode = false, zIndex = 2, isMobile = false }) {
  const activeHotspots = hotspots.filter(h => h.isActive !== false);

  const sortedByX = [...activeHotspots]
    .filter(h => isClickable(h))
    .map(h => ({ id: h.id, cx: getHotspotCentroid(h).cx }))
    .sort((a, b) => a.cx - b.cx);

  const [shimmerDelays, setShimmerDelays] = useState({});
  const anyHoveredRef = useRef(false);
  const sortedKey     = sortedByX.map(h => h.id).join(',');

  useEffect(() => {
    const fire = () => {
      if (anyHoveredRef.current) return;
      const delays = {};
      sortedByX.forEach((h, i) => { delays[h.id] = i * 120; });
      setShimmerDelays(delays);
      setTimeout(() => setShimmerDelays({}), sortedByX.length * 120 + 800);
    };
    const interval = setInterval(fire, 4000);
    return () => clearInterval(interval);
  }, [sortedKey]); // eslint-disable-line

  const [tooltip, setTooltip] = useState(null);
  const tooltipTimeoutRef     = useRef(null);

  const handleShapeHover = useCallback((label, e) => {
    anyHoveredRef.current = true;
    setShimmerDelays({});
    if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    if (label) setTooltip({ label, x: e.clientX, y: e.clientY });
  }, []);

  const handleShapeMove  = useCallback((e) => {
    if (tooltip) setTooltip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
  }, [tooltip]);

  const handleShapeLeave = useCallback(() => {
    anyHoveredRef.current = false;
    tooltipTimeoutRef.current = setTimeout(() => setTooltip(null), 80);
  }, []);

  return (
    <>
      <svg
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex, pointerEvents: 'none' }}
        viewBox={`0 0 ${bpCanvasW} ${bpCanvasH}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs><style>{HOTSPOT_KEYFRAMES}</style></defs>
        {activeHotspots.map(h => (
          <HotspotShape
            key={h.id}
            hotspot={h}
            isClickable={isClickable(h)}
            onClick={onHotspotClick}
            shimmerDelay={showcaseMode ? null : (shimmerDelays[h.id] ?? null)}
            onHover={handleShapeHover}
            onMove={handleShapeMove}
            onLeave={handleShapeLeave}
            bpCanvasW={bpCanvasW}
            bpCanvasH={bpCanvasH}
            showcaseMode={showcaseMode}
          />
        ))}
      </svg>

      {!isMobile && tooltip && ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: tooltip.y - 42, left: tooltip.x, transform: 'translateX(-50%)', pointerEvents: 'none', zIndex: 9999, background: 'rgba(10,14,26,0.88)', color: '#fff', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', padding: '5px 11px', borderRadius: 6, whiteSpace: 'nowrap', border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 4px 12px rgba(0,0,0,0.35)', letterSpacing: '0.02em' }}>
          {tooltip.label}
        </div>,
        document.body,
      )}
    </>
  );
}

// ── EngImageElement ────────────────────────────────────────────────────────────
const IMG_DOT_CSS = `
  @keyframes img-dot-pulse { 0%,100% { transform: translate(-50%,-50%) scale(1); } 50% { transform: translate(-50%,-50%) scale(1.28); } }
  @keyframes img-dot-ring  { 0% { transform: translate(-50%,-50%) scale(1); opacity: 0.7; } 100% { transform: translate(-50%,-50%) scale(2.8); opacity: 0; } }
`;

function ImageLinkDot({ color = '#1075BA' }) {
  return (
    <>
      <style>{IMG_DOT_CSS}</style>
      <div style={{ position: 'absolute', top: '50%', left: '50%', width: 21, height: 21, borderRadius: '50%', border: `1.5px solid ${color}`, boxSizing: 'border-box', pointerEvents: 'none', animation: 'img-dot-ring 2s ease-out 0.4s infinite' }} />
      <div style={{ position: 'absolute', top: '50%', left: '50%', width: 21, height: 21, borderRadius: '50%', background: color, border: '2px solid #ffffff', boxSizing: 'border-box', boxShadow: `0 0 8px ${color}88`, pointerEvents: 'none', animation: 'img-dot-pulse 2s ease-in-out infinite', transform: 'translate(-50%, -50%)' }} />
    </>
  );
}

export function EngImageElement({ hotspot, canvasScale = 1, onHotspotClick }) {
  const isActive = hotspot.isActive !== false;
  if (!isActive) return null;

  const x = hotspot.points?.[0]?.x ?? 10;
  const y = hotspot.points?.[0]?.y ?? 10;
  const w = hotspot.meta?.width    ?? 25;
  const h = hotspot.meta?.height   ?? 14;

  const imageUrl      = hotspot.meta?.imageUrl      ?? null;
  const objectFit     = hotspot.meta?.objectFit     ?? 'cover';
  const bgStrokeColor = hotspot.meta?.bgStrokeColor ?? 'transparent';
  const bgStrokeWidth = (hotspot.meta?.bgStrokeWidth ?? 0) * canvasScale;
  const borderRadius  = (hotspot.meta?.borderRadius  ?? 0) * canvasScale;

  if (!imageUrl) return null;

  const hasLink  = !!(hotspot.hotspotAction?.targetId || hotspot.hotspotAction?.externalUrl);
  const dotColor = hotspot.glowColor || '#1075BA';

  return (
    <div
      onClick={hasLink && onHotspotClick ? () => onHotspotClick(hotspot) : undefined}
      style={{ position: 'absolute', left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%`, zIndex: 5 + (hotspot.order ?? 0), border: bgStrokeWidth > 0 ? `${bgStrokeWidth}px solid ${bgStrokeColor}` : 'none', borderRadius: `${borderRadius}px`, overflow: 'hidden', pointerEvents: hasLink ? 'all' : 'none', cursor: hasLink ? 'pointer' : 'default', userSelect: 'none', boxSizing: 'border-box' }}
    >
      <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit, display: 'block', pointerEvents: 'none' }} />
      {hasLink && <ImageLinkDot color={dotColor} />}
    </div>
  );
}